import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { orderBy, QueryConstraint, where } from "firebase/firestore";
import { FirestoreService } from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  pharmFillMedicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  pharmFillMedicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const pharmFillMedicalPaOrdersSlice = createSlice({
  name: "pharmFillMedicalPaOrders",
  initialState,
  reducers: {
    setPharmFillMedicalPaOrders: (state, action) => {
      state.pharmFillMedicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },

    appendPharmFillMedicalPaOrders: (
      state,
      action: PayloadAction<MedicalPaOrder[]>,
    ) => {
      state.pharmFillMedicalPaOrders = state.pharmFillMedicalPaOrders
        ? [...state.pharmFillMedicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.pharmFillMedicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },

    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },

    resetPagination: (state) => {
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.pharmFillMedicalPaOrders = null;
    },

    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
  },
});

export const {
  setPharmFillMedicalPaOrders,
  appendPharmFillMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
} = pharmFillMedicalPaOrdersSlice.actions;

const fetchTotalDocumentsCount =
  (
    facilityId: string,
    providerId?: string,
    queryConstraints?: QueryConstraint[],
  ) =>
  async (dispatch) => {
    try {
      const allDocs =
        await FirestoreService.getDocumentsByQuery<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          queryConstraints ?? [],
        );

      dispatch(setTotalDocuments(allDocs.length));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      dispatch(setTotalDocuments(0));
      return 0;
    }
  };

export const fetchPharmFillMedicalPaOrdersAfterSearch =
  (facilityId: string, providerId: string, searchTerm?: string) =>
  async (dispatch, getState) => {
    const { pharmFillMedicalPaOrders } = getState();
    if (pharmFillMedicalPaOrders.loading) return;

    dispatch(setLoading(true));

    const searchQueryConstraints: QueryConstraint[] = [
      where("assigned_to.facility_id", "==", facilityId),
      where("assigned_to.provider_id", "==", providerId),
      where("status.financial_review", "==", MedicalPaOrderStatus.PHARM_FILL),
      orderBy("created_at", "desc"),
    ];

    if (searchTerm) {
      searchQueryConstraints.push(
        where("search_index", "array-contains", searchTerm.toLowerCase()),
      );
    }

    await dispatch(
      fetchTotalDocumentsCount(facilityId, providerId, searchQueryConstraints),
    );

    try {
      const unsubscribe = FirestoreService.fetchQueryCollection<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        (data) => {
          dispatch(setPharmFillMedicalPaOrders(data));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        ...searchQueryConstraints,
      );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchPharmFillMedicalPaOrders =
  (facilityId: string, pageSize: number, providerId?: string) =>
  async (dispatch, getState) => {
    const { pharmFillMedicalPaOrders } = getState();
    if (pharmFillMedicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    const queryConstraints: QueryConstraint[] = [orderBy("created_at", "desc")];

    try {
      queryConstraints.push(where("assigned_to.facility_id", "==", facilityId));
      if (providerId) {
        queryConstraints.push(
          where("assigned_to.provider_id", "==", providerId),
        );
      }
      queryConstraints.push(
        where("status.financial_review", "==", MedicalPaOrderStatus.PHARM_FILL),
      );

      await dispatch(
        fetchTotalDocumentsCount(facilityId, providerId, queryConstraints),
      );

      FirestoreService.paginateQuery<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setPharmFillMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        undefined,
        queryConstraints,
      );
    } catch (error) {
      logDataToConsole("Error in fetchPharmFillMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMorePharmFillMedicalPaOrders =
  (facilityId: string, pageSize: number, providerId?: string) =>
  async (dispatch, getState) => {
    const { pharmFillMedicalPaOrders } = getState();

    if (
      pharmFillMedicalPaOrders.loading ||
      pharmFillMedicalPaOrders.showinLineLoader ||
      pharmFillMedicalPaOrders.currentlyLoadedCount ===
        pharmFillMedicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    const queryConstraints: QueryConstraint[] = [orderBy("created_at", "desc")];

    try {
      queryConstraints.push(where("assigned_to.facility_id", "==", facilityId));
      if (providerId) {
        queryConstraints.push(
          where("assigned_to.provider_id", "==", providerId),
        );
      }
      queryConstraints.push(
        where("status.financial_review", "==", MedicalPaOrderStatus.PHARM_FILL),
      );

      const unsubscribe = FirestoreService.paginateQuery<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(appendPharmFillMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(setinLineLoader(false));
        },
        (error) => {
          dispatch(setError(error.message));
          dispatch(setinLineLoader(false));
        },
        pharmFillMedicalPaOrders.lastVisible,
        queryConstraints,
      );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { pharmFillMedicalPaOrders } = state;

  return (
    pharmFillMedicalPaOrders.currentlyLoadedCount <
      pharmFillMedicalPaOrders.totalDocuments &&
    endIndex === pharmFillMedicalPaOrders.currentlyLoadedCount - 1
  );
};

export default pharmFillMedicalPaOrdersSlice.reducer;
